import { useDisplayValueWithUnit } from '@app/src/hooks/useDisplayValueWithUnit'
import { usePeriodName } from '@app/src/hooks/usePeriodName'
import {
  DONT_HAVE_THIS_COLOR,
  notAnsweredStyling,
  REPORTED_COLOR,
} from '@app/src/pages/ResourceCollection/Collections/DataHub/DataInsights/Charts/CategoryChartsConfigs/categoryChartConfigsUtils'
import type { StatisticsSummary } from '@app/src/pages/ResourceCollection/Collections/DataHub/DataInsights/Charts/DataHubSummaryChart'
import {
  NumberMetricSelector,
  SelectedMetric,
} from '@app/src/pages/ResourceCollection/Collections/DataHub/DataInsights/NumberMetricSelector'
import type { Unit } from '@app/src/types/resourceExplorer'
import {
  getMinMaxOnYAxisNumber,
  getUnitDisplayText,
  numberToString,
  numberToStringWithUnit,
  shouldShowSum,
} from '@app/src/utils/statisticsUtils'
import { Box, darken } from '@mui/material'
import { useTheme } from '@mui/styles'
import type { BarSeriesOption, EChartsOption } from 'echarts'
import ReactEChartsCore from 'echarts-for-react/lib/core'
import * as echarts from 'echarts/core'
import { max } from 'lodash'
import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import type { StatisticsForQuestionPerGroup } from '../../../DataHubScene'
import type { UserSelection } from '../DataInsightsModalContext'
import { useDataInsightsModal } from '../DataInsightsModalContext'
import type { EchartClickItemData } from './OptionMultiGroupChart'
import { SummaryChartDataTypes } from './PeriodChartsConfigs/usePeriodSummaryChartConfigs'

interface NumberMultiGroupLineChartProps {
  statistics: StatisticsForQuestionPerGroup
  statisticsSummary: StatisticsSummary
  height: string
  echartsRef: React.MutableRefObject<ReactEChartsCore | null>
  unit: Unit
}

export const NumberMultiGroupLineChart: React.FC<NumberMultiGroupLineChartProps> = ({
  statistics,
  statisticsSummary,
  height,
  echartsRef,
  unit,
}) => {
  const { typography, palette } = useTheme()
  const { getOptimalUnitForMultipleValues } = useDisplayValueWithUnit()
  const { formatMessage } = useIntl()
  const { formatPeriodName } = usePeriodName()
  const showSum = shouldShowSum(unit)
  const [selectedMetric, setSelectedMetric] = useState<SelectedMetric>(
    showSum ? SelectedMetric.Sum : SelectedMetric.Average,
  )

  const { openDataInsightsModal } = useDataInsightsModal()

  const events = useMemo(
    () => ({
      click: ({ data }: { data: EchartClickItemData }) => {
        const userSelection: UserSelection = {
          groupName: data.groupName,
          groupId: data.groupId,
          summaryType: data.summaryType ? [data.summaryType] : [],
        }
        openDataInsightsModal(statistics, userSelection)
      },
    }),
    [openDataInsightsModal, statistics],
  )

  const questionTitle = statistics.title
  const resultsForGroup = statistics.resultForGroups

  const options: EChartsOption = useMemo(() => {
    const data = resultsForGroup.map(r => ({
      groupName: r.groupName,
      groupId: r.groupId,
      value: selectedMetric === SelectedMetric.Average ? r.numberResult?.average : r.numberResult?.sum,
    }))

    const values = data.flatMap(d => (Number(d.value) ? Number(d.value) : []))
    const optimalUnit = getOptimalUnitForMultipleValues(values, unit)
    const { min: valueAxisMin, max: valueAxisMax } = getMinMaxOnYAxisNumber(values, selectedMetric, optimalUnit)

    const existingOptions = resultsForGroup.map(f => f.groupName)

    const summaryValues = statisticsSummary.resultForGroups.flatMap(res => [
      res.companiesAnswered,
      res.companiesAnsweredDontHaveThis,
      res.companiesNotAnswered,
    ])
    const summaryAxisMax = Math.round((max(summaryValues) ?? 0) * 2.5)

    const reportedSeries: BarSeriesOption = {
      name: formatMessage(
        { id: 'statistics.dataInsights.reportedCount' },
        {
          count: String(statisticsSummary.resultForGroups[0]?.companiesAnswered ?? 0),
          isSinglePeriod: statisticsSummary.resultForGroups.length === 1,
        },
      ),
      type: 'bar',
      yAxisIndex: 1,
      barCategoryGap: '35%',
      data: statisticsSummary.resultForGroups.map(r => ({
        name: formatPeriodName(r.groupName),
        value: r.companiesAnswered,
        groupName: r.groupName,
        summaryType: SummaryChartDataTypes.CompaniesAnswered,
        emphasis: { itemStyle: { color: darken(REPORTED_COLOR, 0.2) } },
      })),
      color: REPORTED_COLOR,
    }

    const questionSkippedSeries: BarSeriesOption = {
      name: formatMessage({ id: 'statistics.dataInsights.dontHaveThis' }),
      type: 'bar',
      yAxisIndex: 1,
      barCategoryGap: '35%',
      emphasis: { itemStyle: { color: darken(DONT_HAVE_THIS_COLOR, 0.2) } },
      data: resultsForGroup.map(g => {
        const summaryData = statisticsSummary.resultForGroups.find(s => s.groupName === g.groupName)
        return {
          value: summaryData?.companiesAnsweredDontHaveThis || 0,
          name: formatPeriodName(g.groupName),
          groupName: g.groupName,
          summaryType: SummaryChartDataTypes.CompaniesAnsweredDontHaveThis,
          answersSelected: [],
          label: {
            position: 'top',
          },
        }
      }),
      color: DONT_HAVE_THIS_COLOR,
    }

    const noResponseSeries: BarSeriesOption = {
      name: formatMessage({ id: 'statistics.dataInsights.noResponse' }),
      type: 'bar',
      yAxisIndex: 1,
      barCategoryGap: '35%',
      ...notAnsweredStyling(palette),
      data: resultsForGroup.map(g => {
        const summaryData = statisticsSummary.resultForGroups.find(s => s.groupName === g.groupName)
        return {
          value: summaryData?.companiesNotAnswered || 0,
          name: formatPeriodName(g.groupName),
          groupName: g.groupName,
          summaryType: SummaryChartDataTypes.CompaniesNotAnswered,
          answersSelected: [],
          label: {
            position: 'top',
          },
        }
      }),
    }

    return {
      tooltip: { show: true },
      legend: {
        icon: 'circle',
        bottom: 0,
        left: 0,
        selectedMode: false,
        textStyle: {
          fontFamily: typography.fontFamily,
          fontSize: typography.body2.fontSize,
        },
      },
      grid: {
        containLabel: true,
        left: 30,
        right: 60,
        top: 30,
        bottom: 50,
      },
      xAxis: {
        type: 'category',
        data: existingOptions,
        axisLabel: {
          interval: 0,
          fontFamily: typography.fontFamily,
          fontSize: typography.body2.fontSize,
        },
      },
      yAxis: [
        {
          type: 'value',
          axisLabel: {
            formatter: value => numberToString(value, unit, optimalUnit),
          },
          name: getUnitDisplayText(optimalUnit, formatMessage({ id: 'statistics.dataInsights.count' })),
          nameLocation: 'middle',
          nameGap: 35,
          nameTextStyle: {
            fontFamily: typography.fontFamily,
            fontSize: typography.body2.fontSize,
          },
          min: valueAxisMin,
          max: valueAxisMax,
        },
        {
          type: 'value',
          name: formatMessage({ id: 'statistics.dataInsights.summaryCount' }),
          nameLocation: 'middle',
          nameGap: 35,
          nameTextStyle: {
            fontFamily: typography.fontFamily,
            fontSize: typography.body2.fontSize,
          },
          position: 'right',
          min: 0,
          max: summaryAxisMax,
          minInterval: 1,
          axisLine: {},
          axisTick: {
            show: false,
          },
          splitLine: {
            show: false,
          },
        },
      ],
      series: [
        {
          name: getUnitDisplayText(optimalUnit, questionTitle),
          type: 'line',
          yAxisIndex: 0,
          tooltip: {
            valueFormatter: value => numberToStringWithUnit(Number(value), unit, optimalUnit),
          },
          lineStyle: {
            width: 2,
            color: palette.visualization[1],
          },
          symbolSize: 10,
          symbol: 'circle',
          itemStyle: {
            borderColor: 'white',
            borderWidth: 2,
            color: palette.visualization[1],
          },
          label: {
            show: true,
            position: 'top',
            color: 'black',
            formatter: value => numberToStringWithUnit(Number(value.value), unit, optimalUnit),
            fontFamily: typography.fontFamily,
            fontSize: 13,
            fontWeight: 500,
            backgroundColor: 'rgba(255, 255, 255, 0.40)',
            padding: [7, 10],
            borderRadius: 3,
          },
          emphasis: {
            focus: 'none',
          },
          data: data,
        },
        reportedSeries,
        // Only include series that have data
        ...(statisticsSummary.resultForGroups.some(res => res.companiesAnsweredDontHaveThis > 0)
          ? [questionSkippedSeries]
          : []),
        ...(statisticsSummary.resultForGroups.some(res => res.companiesNotAnswered > 0) ? [noResponseSeries] : []),
      ],
    }
  }, [
    resultsForGroup,
    statisticsSummary,
    getOptimalUnitForMultipleValues,
    unit,
    typography.fontFamily,
    typography.body2.fontSize,
    formatMessage,
    formatPeriodName,
    palette,
    questionTitle,
    selectedMetric,
  ])

  return (
    <Box height={height} display="flex" flexDirection="column">
      <NumberMetricSelector selectedMetric={selectedMetric} setSelectedMetric={setSelectedMetric} showSum={showSum} />
      <ReactEChartsCore
        onEvents={events}
        option={options}
        echarts={echarts}
        style={{ minWidth: '100%', flexGrow: 1 }}
        ref={echartsRef}
      />
    </Box>
  )
}
